import {
  AssessmentIcon,
  CustomIconComponent,
  ArrowBackIcon,
  AttachMoneyIcon,
  BarChartOutlinedIcon,
  BusinessIcon,
  DateRangeIcon,
  EventIcon,
  FolderOpenIcon,
  InsertLinkIcon as LinkIcon,
  ListAltIcon,
  AccountBalanceIcon,
  AccountBalanceWalletIcon,
  QuizIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  ViewListIcon,
  PaymentsIcon,
  DownloadIcon,
  FilterListIcon,
  ReceiptLongIcon,
} from 'components/common/Icon';
import { SvgIcon } from '@mui/material';
import { Routes } from 'fnRoutes';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import LockClockIcon from '@mui/icons-material/LockClock';
import i18n from 'translations/i18n';
import {
  ACCOUNTING_BUSINESS_HOME_FLAG,
  ACCOUNTING_BUSINESS_MILES_FLAG,
  ACCOUNTING_FLAG,
  ADMIN_BUSINESS_USE_OF_HOME,
  ADMIN_BUSINESSMILES,
  ADMIN_XERO_CONNECTION_HISTORY,
  CHECK_IN_SURVEY_FLAG,
  HURDLR_DATA_SOURCE,
  INVOICING_FLAG,
  IRS_MILEAGE_RATE,
  NEW_ONBOARDING_FLAG,
  OIL_CUSTOMER_FLAG,
  PAYROLL_FLAG,
  PAYROLL_ONBOARDING_FLAG,
  VENDOR_MANAGEMENT_FLAG,
} from 'hooks/useFeatureFlag';
import {
  isBusinessTaxOnly,
  isBusinessBusinessInABox,
  isBusinessHasHurdlrConnected,
  isBusinessPayrollEnabled,
} from 'components/ProgressTracker/util';
import { Business } from 'models/business';
import { TransactionDataSource } from 'services/features';

// feature flag controls who can view
// actionPermission controls who can click on item
interface IActionBase {
  id: string;
  Icon?: typeof SvgIcon | CustomIconComponent;
  type?: string;
  text: string;
  path: string;
  disabled?: boolean;
  featureFlag?: string | string[]; // FLAGS.DEV | FLAGS.BETA | FLAGS.LAUNCH | ''
  actionPermission?: string; // FLAGS.DEV | FLAGS.BETA | FLAGS.LAUNCH | ''
  hiddenForStatus?: string[];
  disabledForStatus?: string[];
  shouldRender?: (business?: Business) => boolean;
  shouldDisable?: (business?: Business) => boolean;
}

export type ISubAction = Omit<IActionBase, 'Icon'>;
export interface IAction extends IActionBase {
  subActions?: Array<ISubAction>;
}
export const businessIsAny =
  (arr: Array<(business?: Business) => boolean>) => (business?: Business) =>
    arr.some((fn) => fn(business));

const CUSTOMER_ACTIONS: IAction[] = [
  {
    id: 'welcome',
    Icon: BarChartOutlinedIcon,
    text: i18n.t('navigation.dashboard'),
    path: Routes.WELCOME,
  },
  {
    id: 'accounting',
    Icon: ListAltIcon,
    text: i18n.t('navigation.accounting'),
    path: '',
    featureFlag: ACCOUNTING_FLAG,
    disabledForStatus: ['NEW'],
    shouldDisable: businessIsAny([isBusinessBusinessInABox]),
    subActions: [
      {
        id: 'bankAccounts',
        text: i18n.t('navigation.bankAccounts'),
        path: Routes.BANK_ACCOUNTS,
        featureFlag: HURDLR_DATA_SOURCE,
        disabledForStatus: ['NEW'],
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'unknownTransactions',
        text: i18n.t('navigation.unknownTransactions'),
        path: Routes.ACCOUNTING_UNKNOWN_TRANSACTIONS,
        featureFlag: ACCOUNTING_FLAG,
        disabledForStatus: ['NEW'],
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'allTransactions',
        text: i18n.t('navigation.allTransactions'),
        path: Routes.ACCOUNTING_ALL_TRANSACTIONS,
        featureFlag: ACCOUNTING_FLAG,
        disabledForStatus: ['NEW'],
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'addTransaction',
        text: i18n.t('navigation.addTransaction'),
        path: Routes.ACCOUNTING_ADD_TRANSACTION,
        featureFlag: ACCOUNTING_FLAG,
        disabledForStatus: ['NEW'],
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'vendors',
        text: i18n.t('navigation.vendors'),
        path: Routes.VENDORS,
        disabledForStatus: ['NEW'],
        featureFlag: VENDOR_MANAGEMENT_FLAG,
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'businessMiles',
        text: i18n.t('navigation.businessMiles'),
        path: Routes.ACCOUNTING_BUSINESS_MILES,
        featureFlag: ACCOUNTING_BUSINESS_MILES_FLAG,
        actionPermission: ACCOUNTING_BUSINESS_MILES_FLAG,
        hiddenForStatus: ['NEW'],
        shouldDisable: businessIsAny([isBusinessBusinessInABox]),
      },
      {
        id: 'businessUseOfHome',
        text: i18n.t('navigation.businessUseOfHome'),
        path: Routes.ACCOUNTING_BUSINESS_USE_OF_HOME,
        featureFlag: ACCOUNTING_BUSINESS_HOME_FLAG,
        actionPermission: ACCOUNTING_BUSINESS_HOME_FLAG,
        hiddenForStatus: ['NEW'],
        shouldDisable: businessIsAny([isBusinessBusinessInABox]),
      },
      {
        id: 'ytdAccounting',
        text: i18n.t('navigation.ytdAccounting'),
        path: Routes.ACCOUNT_YTD,
        shouldRender: businessIsAny([isBusinessTaxOnly]),
      },
      {
        id: 'reports',
        text: i18n.t('navigation.reports'),
        path: Routes.ACCOUNTING_REPORTS,
        hiddenForStatus: ['NEW'],
        featureFlag: TransactionDataSource.Hurdlr,
        shouldRender: businessIsAny([isBusinessHasHurdlrConnected]),
      },
    ],
  },
  {
    id: 'payroll',
    Icon: AccountBalanceWalletIcon,
    text: i18n.t('navigation.payroll'),
    path: '',
    featureFlag: PAYROLL_FLAG,
    disabledForStatus: ['NEW'],
    shouldDisable: businessIsAny([isBusinessTaxOnly, isBusinessBusinessInABox]),
    shouldRender: businessIsAny([isBusinessPayrollEnabled]),
    subActions: [
      {
        id: 'payrollSummaries',
        text: i18n.t('navigation.payrollSummaries'),
        path: Routes.PAYROLL,
        featureFlag: PAYROLL_FLAG,
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'payrollSettings',
        text: i18n.t('navigation.payrollSettings'),
        path: Routes.PAYROLL_SETTINGS,
        featureFlag: PAYROLL_FLAG,
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
    ],
  },
  {
    id: 'invoicing',
    Icon: ReceiptLongIcon,
    text: i18n.t('navigation.invoicing'),
    path: Routes.INVOICING,
    hiddenForStatus: ['NEW'],
    featureFlag: [TransactionDataSource.Hurdlr, INVOICING_FLAG],
    shouldDisable: businessIsAny([isBusinessTaxOnly, isBusinessBusinessInABox]),
  },
  {
    id: 'taxes',
    Icon: AttachMoneyIcon,
    text: i18n.t('navigation.taxes'),
    path: '',
    disabledForStatus: ['NEW'],
    shouldDisable: businessIsAny([isBusinessBusinessInABox]),
    subActions: [
      {
        id: 'oil-list',
        text: i18n.t('navigation.oil'),
        path: Routes.OIL,
        hiddenForStatus: ['NEW'],
        featureFlag: OIL_CUSTOMER_FLAG,
        shouldDisable: businessIsAny([isBusinessBusinessInABox]),
      },
      {
        id: 'tax-payments',
        text: i18n.t('navigation.taxPayments'),
        path: Routes.TAXES,
        hiddenForStatus: ['NEW'],
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
      {
        id: 'tax-form',
        text: i18n.t('navigation.tax-form'),
        path: Routes.TAX_LIABILITY_FORM,
        hiddenForStatus: ['NEW'],
        featureFlag: CHECK_IN_SURVEY_FLAG,
        shouldDisable: businessIsAny([
          isBusinessTaxOnly,
          isBusinessBusinessInABox,
        ]),
      },
    ],
  },
  {
    id: 'documents',
    Icon: FolderOpenIcon,
    text: i18n.t('navigation.documents'),
    path: Routes.DOCUMENTS,
  },
  {
    id: 'onboarding',
    Icon: ViewListIcon,
    text: i18n.t('navigation.onboarding'),
    path: Routes.ONBOARDING,
    featureFlag: NEW_ONBOARDING_FLAG,
    actionPermission: NEW_ONBOARDING_FLAG,
  },
];

export const CUSTOMER_PAYROLL_SETUP_ACTIONS = CUSTOMER_ACTIONS.map((item) => {
  if (item.id === 'payroll') {
    return {
      id: 'payroll',
      Icon: AccountBalanceWalletIcon,
      text: i18n.t('navigation.payroll'),
      path: Routes.PAYROLL_ONBOARDING,
      featureFlag: PAYROLL_ONBOARDING_FLAG,
    };
  }
  return item;
});

export const CUSTOMER_PAYROLL_DISABLED_ACTIONS = CUSTOMER_ACTIONS.map(
  (item) => {
    if (item.id === 'payroll') {
      return {
        id: 'payroll',
        Icon: AccountBalanceWalletIcon,
        text: 'Payroll',
        path: Routes.PAYROLL,
        featureFlag: PAYROLL_FLAG,
        disabled: true,
      };
    }
    return item;
  },
);

const generateAdminAccountActions = (accountId: string): IAction[] => [
  {
    id: 'accounts',
    Icon: ArrowBackIcon,
    type: 'account',
    text: i18n.t('navigation.accounts'),
    path: Routes.ADMIN_HOME,
  },
  {
    id: 'overview',
    text: i18n.t('navigation.overview'),
    path: '',
    type: 'account',
    subActions: [
      {
        id: 'accountProfile',
        type: 'account',
        text: i18n.t('navigation.accountProfile'),
        path: `/dashboard/accounts/${accountId}/accountProfile`,
      },
      {
        id: 'plan',
        type: 'account',
        text: i18n.t('navigation.plan'),
        path: `/dashboard/accounts/${accountId}/plan`,
      },
      {
        id: 'events',
        type: 'account',
        text: i18n.t('navigation.upcomingEvents'),
        path: `/dashboard/accounts/${accountId}/events`,
      },
    ],
  },
  {
    id: 'accountDocuments',
    type: 'account',
    text: i18n.t('navigation.accountDocuments'),
    path: `/dashboard/accounts/${accountId}/documents`,
  },
  {
    id: 'taxesTop',
    text: i18n.t('navigation.taxes'),
    path: '',
    type: 'account',
    subActions: [
      {
        id: 'taxLiability',
        type: 'account',
        text: i18n.t('navigation.taxLiability'),
        path: `/dashboard/accounts/${accountId}/taxLiability`,
      },
      {
        id: 'open-item-list',
        type: 'account',
        text: i18n.t('navigation.oil'),
        path: `/dashboard/accounts/${accountId}/open-item-list`,
      },
      {
        id: 'tax-filing',
        type: 'account',
        text: i18n.t('navigation.taxFiling'),
        path: `/dashboard/accounts/${accountId}/tax-filing`,
      },
      {
        id: 'taxes',
        type: 'account',
        text: i18n.t('navigation.taxPayments'),
        path: `/dashboard/accounts/${accountId}/taxes`,
      },
      {
        id: 'checkin',
        type: 'account',
        text: i18n.t('navigation.checkIn'),
        path: `/dashboard/accounts/${accountId}/checkin`,
      },
    ],
  },
  {
    id: 'user-settings',
    type: 'account',
    text: i18n.t('navigation.userSettings'),
    path: `/dashboard/accounts/${accountId}/user-settings`,
  },
];

const generateAdminBusinessActions = (
  accountId: string,
  businessId: string,
): IAction[] => [
  {
    id: 'overview',
    text: i18n.t('navigation.overview'),
    path: '',
    type: 'business',
    subActions: [
      {
        id: 'businessProfile',
        type: 'business',
        text: i18n.t('navigation.businessProfile'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/businessProfile`,
      },
      {
        id: 'progress-tracker',
        type: 'business',
        text: i18n.t('navigation.progressTracker'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/progress-tracker`,
      },
      {
        id: 'company-set-up',
        type: 'business',
        text: i18n.t('navigation.companySetUp'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/company-set-up`,
      },
    ],
  },
  {
    id: 'businessDocuments',
    type: 'business',
    text: i18n.t('navigation.businessDocuments'),
    path: `/dashboard/accounts/${accountId}/businesses/${businessId}/documents`,
  },
  {
    id: 'taxesTop',
    text: i18n.t('navigation.taxes'),
    path: '',
    type: 'business',
    subActions: [
      {
        id: 'open-item-list',
        type: 'business',
        text: i18n.t('navigation.oil'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/open-item-list`,
      },
      {
        id: 'tax-filing',
        type: 'business',
        text: i18n.t('navigation.taxFiling'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/tax-filing`,
      },
      {
        id: 'taxes',
        type: 'business',
        text: i18n.t('navigation.taxPayments'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/taxes`,
      },
      {
        id: 'checkin',
        type: 'business',
        text: i18n.t('navigation.checkIn'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/checkin`,
      },
    ],
  },
  {
    id: 'accountingTop',
    text: i18n.t('navigation.accounting'),
    path: '',
    type: 'business',
    subActions: [
      {
        id: 'vendors',
        type: 'business',
        text: i18n.t('navigation.vendors'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/vendors`,
      },
      {
        id: 'hurdlr',
        type: 'business',
        text: i18n.t('navigation.hurdlr'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/hurdlr`,
      },
      {
        id: 'businessUseOfHome',
        type: 'business',
        text: i18n.t('navigation.businessUseOfHome'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/businessUseOfHome`,
        featureFlag: ADMIN_BUSINESS_USE_OF_HOME,
      },
      {
        id: 'businessMiles',
        type: 'business',
        text: i18n.t('navigation.businessMiles'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/businessMiles`,
        featureFlag: ADMIN_BUSINESSMILES,
      },
      {
        id: 'xeroConnectionHistory',
        type: 'business',
        text: i18n.t('navigation.xeroConnectionHistory'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/xeroConnectionHistory`,
        featureFlag: ADMIN_XERO_CONNECTION_HISTORY,
      },
      {
        id: 'account-ytd',
        type: 'business',
        text: i18n.t('navigation.ytdAccounting'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/account-ytd`,
      },
    ],
  },
  {
    id: 'payrollTop',
    text: i18n.t('navigation.payroll'),
    path: '',
    type: 'business',
    subActions: [
      {
        id: 'payroll',
        type: 'business',
        text: i18n.t('navigation.payroll'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/payroll`,
      },
      {
        id: 'officers',
        type: 'business',
        text: i18n.t('navigation.officers'),
        path: `/dashboard/accounts/${accountId}/businesses/${businessId}/officers`,
      },
    ],
  },
];

const ADMIN_ACTIONS = [
  {
    id: 'accounts',
    type: 'admin',
    Icon: AccountBalanceWalletIcon,
    text: i18n.t('navigation.accounts'),
    path: Routes.ADMIN_HOME,
  },
  {
    id: 'hurdlr',
    type: 'admin',
    Icon: AccountBalanceIcon,
    text: i18n.t('navigation.hurdlr'),
    path: Routes.HURDLR,
  },
  {
    id: 'worknet',
    type: 'admin',
    Icon: AutoAwesomeRoundedIcon,
    text: i18n.t('navigation.worknet'),
    path: Routes.WORKNET,
  },
  {
    id: 'oilTemplates',
    type: 'admin',
    Icon: FilterListIcon,
    text: i18n.t('navigation.oilTemplates'),
    path: Routes.OIL_TEMPLATES,
  },
  {
    id: 'upcomingEvents',
    type: 'admin',
    Icon: EventIcon,
    text: i18n.t('navigation.upcomingEvents'),
    path: Routes.UPCOMING_EVENTS,
  },
  {
    id: 'taxBracket',
    type: 'admin',
    Icon: TrendingUpIcon,
    text: i18n.t('navigation.taxBracket'),
    path: Routes.TAX_BRACKET,
  },
  {
    id: 'standardDeduction',
    type: 'admin',
    Icon: TrendingDownIcon,
    text: i18n.t('navigation.standardDeduction'),
    path: Routes.STANDARD_DEDUCTION,
  },
  {
    id: 'industryOptions',
    type: 'admin',
    Icon: BusinessIcon,
    text: i18n.t('navigation.industryOptions'),
    path: Routes.INDUSTRY_OPTIONS,
  },
  {
    id: 'payrollDeductions',
    type: 'admin',
    Icon: AttachMoneyIcon,
    text: i18n.t('navigation.payrollDeductions'),
    path: Routes.PAYROLL_DEDUCTIONS,
  },
  {
    id: 'taxDeadline',
    type: 'admin',
    Icon: LockClockIcon,
    text: i18n.t('navigation.taxDeadline'),
    path: Routes.TAX_DEADLINE,
  },
];

const SUPERADMIN_ACTIONS = [
  {
    id: 'irs',
    type: 'superAdmin',
    Icon: AssessmentIcon,
    text: i18n.t('navigation.irsMileageRate'),
    path: Routes.SUPERADMIN_IRS_RATE,
    featureFlag: IRS_MILEAGE_RATE,
  },
  {
    id: 'csmMeetingLink',
    type: 'superAdmin',
    Icon: LinkIcon,
    text: i18n.t('navigation.csmMeetingLink'),
    path: Routes.CSM_MEETING_LINK,
  },
  {
    id: 'activeSurvey',
    type: 'superAdmin',
    Icon: QuizIcon,
    text: i18n.t('navigation.activeSurvey'),
    path: Routes.ACTIVE_SURVEY,
  },
  {
    id: 'activeTaxYears',
    type: 'superAdmin',
    Icon: DateRangeIcon,
    text: i18n.t('navigation.activeTaxYears'),
    path: Routes.ACTIVE_TAX_YEARS,
  },
  {
    id: 'taxLiability',
    type: 'superAdmin',
    Icon: PaymentsIcon,
    text: i18n.t('navigation.taxLiability'),
    path: Routes.TAX_LIABILITY,
  },
  {
    id: 'vendorImport',
    type: 'superAdmin',
    Icon: DownloadIcon,
    text: i18n.t('navigation.vendorsImport'),
    path: Routes.VENDORS_IMPORT,
  },
];

export {
  CUSTOMER_ACTIONS,
  generateAdminAccountActions,
  generateAdminBusinessActions,
  ADMIN_ACTIONS,
  SUPERADMIN_ACTIONS,
};
